import { Loader2 } from 'lucide-react';
import {
  PortalDsButton,
  portalDs,
} from 'src/components/portal-design-system';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from 'src/components/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import { formatDisplayDateTime } from 'src/helpers/date.helpers';
import { cn } from 'src/lib/utils';
import type { DataJobRunDetail } from '../types/data-jobs.types';
import { DataJobStatusBadgeV2 } from './data-job-status-badge-v2';

function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null) {
    return '—';
  }
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `${minutes}m ${remainder}s`;
}

interface DataJobRunSheetV2Props {
  open: boolean;
  run: DataJobRunDetail | null | undefined;
  isLoading?: boolean;
  isRerunning?: boolean;
  rerunError?: string | null;
  onOpenChange: (open: boolean) => void;
  onRerun?: () => void;
}

export function DataJobRunSheetV2({
  open,
  run,
  isLoading = false,
  isRerunning = false,
  rerunError = null,
  onOpenChange,
  onRerun,
}: DataJobRunSheetV2Props) {
  const canRerun = Boolean(
    run
    && ['success', 'warning', 'failed', 'interrupted'].includes(run.status)
    && onRerun,
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 overflow-y-auto bg-white p-0 sm:max-w-2xl"
      >
        <div className={portalDs.sheetHeader}>
          <SheetHeader className="space-y-1 text-left">
            <SheetTitle className={portalDs.sheetTitle}>
              {run?.job_label ?? 'Run detail'}
            </SheetTitle>
            <SheetDescription className={portalDs.sheetDescription}>
              Inspect parameters, step progress, and validation outcomes for this run.
            </SheetDescription>
          </SheetHeader>
        </div>

        <div className="flex-1 space-y-8 px-6 py-5">
          {isLoading ? (
            <div className={cn('flex items-center gap-2 py-8', portalDs.bodySm)} role="status">
              <Loader2 className="size-4 animate-spin text-[#9CA3AF]" />
              Loading run detail…
            </div>
          ) : null}

          {!isLoading && run ? (
            <>
              <section className="space-y-4">
                <div className="flex flex-wrap items-center gap-3">
                  <DataJobStatusBadgeV2 status={run.status} />
                  <span className={cn(portalDs.bodySm, 'capitalize')}>
                    {run.trigger_source}
                  </span>
                  {run.triggered_by_display ? (
                    <span className={portalDs.bodySm}>
                      by
                      {' '}
                      {run.triggered_by_display}
                    </span>
                  ) : null}
                </div>

                <dl className="grid grid-cols-2 gap-4 text-sm">
                  {[
                    ['Active date', run.date_range ?? run.active_date ?? '—'],
                    ['Duration', formatDuration(run.duration_seconds)],
                    ['Created', formatDisplayDateTime(run.created_at)],
                    ['Finished', run.finished_at ? formatDisplayDateTime(run.finished_at) : '—'],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <dt className={portalDs.filterLabel}>{label}</dt>
                      <dd className="mt-1 text-[13.5px] text-[#0D0F1A]">{value}</dd>
                    </div>
                  ))}
                  <div className="col-span-2">
                    <dt className={portalDs.filterLabel}>Summary</dt>
                    <dd className="mt-1 whitespace-pre-wrap text-[13.5px] text-[#0D0F1A]">
                      {run.summary ?? run.error_summary ?? '—'}
                    </dd>
                  </div>
                </dl>

                {canRerun ? (
                  <div className="space-y-2">
                    <PortalDsButton
                      type="button"
                      variant="secondary"
                      onClick={onRerun}
                      disabled={isRerunning}
                    >
                      {isRerunning ? 'Starting rerun…' : 'Rerun'}
                    </PortalDsButton>
                    {rerunError ? (
                      <p className="text-xs text-[#DC2626]" role="alert">{rerunError}</p>
                    ) : null}
                  </div>
                ) : null}
              </section>

              <section className={portalDs.sheetSection}>
                <h3 className={portalDs.sheetSectionTitle}>Parameters</h3>
                <pre className="overflow-x-auto rounded-lg border border-[#EDEEF2] bg-[#F7F8FA] p-3 font-mono text-xs text-[#252840]">
                  {JSON.stringify(run.parameters, null, 2)}
                </pre>
              </section>

              <section className={portalDs.sheetSection}>
                <h3 className={portalDs.sheetSectionTitle}>Step results</h3>
                {run.step_runs.length ? (
                  <div className={portalDs.tableContainer}>
                    <Table>
                      <TableHeader>
                        <TableRow className={portalDs.tableHead}>
                          {['Step', 'Status', 'Duration', 'Rows', 'Error'].map((header) => (
                            <TableHead key={header} className={portalDs.tableHeadCell}>
                              {header}
                            </TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {run.step_runs.map((step) => (
                          <TableRow key={step.id} className={portalDs.tableBodyRow}>
                            <TableCell className={portalDs.tableCell}>{step.label}</TableCell>
                            <TableCell className={portalDs.tableCell}>
                              <DataJobStatusBadgeV2 status={step.status} />
                            </TableCell>
                            <TableCell className={cn(portalDs.tableCell, portalDs.tableCellMuted)}>
                              {formatDuration(step.duration_seconds)}
                            </TableCell>
                            <TableCell className={cn(portalDs.tableCell, portalDs.tableCellMuted)}>
                              {step.row_count ?? '—'}
                            </TableCell>
                            <TableCell className={cn(portalDs.tableCell, portalDs.tableCellMuted, 'max-w-xs truncate')}>
                              {step.error_message ?? '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <p className={portalDs.bodySm}>No step results recorded yet.</p>
                )}
              </section>

              <section className={portalDs.sheetSection}>
                <h3 className={portalDs.sheetSectionTitle}>Validation results</h3>
                {run.validation_results.length ? (
                  <div className={portalDs.tableContainer}>
                    <Table>
                      <TableHeader>
                        <TableRow className={portalDs.tableHead}>
                          {['Target', 'Check', 'Status', 'Value', 'Threshold'].map((header) => (
                            <TableHead key={header} className={portalDs.tableHeadCell}>
                              {header}
                            </TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {run.validation_results.map((result) => (
                          <TableRow key={result.id} className={portalDs.tableBodyRow}>
                            <TableCell className={portalDs.tableCell}>{result.target}</TableCell>
                            <TableCell className={cn(portalDs.tableCell, portalDs.tableCellMuted)}>
                              {result.check_type}
                            </TableCell>
                            <TableCell className={portalDs.tableCell}>
                              <DataJobStatusBadgeV2 status={result.status} />
                            </TableCell>
                            <TableCell className={cn(portalDs.tableCell, portalDs.tableCellMuted)}>
                              {result.value == null ? '—' : String(result.value)}
                            </TableCell>
                            <TableCell className={cn(portalDs.tableCell, portalDs.tableCellMuted)}>
                              {result.threshold == null ? '—' : String(result.threshold)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <p className={portalDs.bodySm}>No validation results recorded.</p>
                )}
              </section>
            </>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}
